import { useMemo } from 'react';
import { PRODUCT_TABS_ID } from '~/features/product-details/constants';
import { usePDPActions, usePDPData } from '~/features/product-details/context';
import { ProductTab } from '~/features/product-details/types';
import { scrollToElementById } from '~/features/product-details/utilities';
import { useTranslation } from '~/shared/utils/translation';
import { ProductInfoLabelProps } from '../components/ProductInfoLabel/ProductInfoLabel';

const ENERGY_LABELS = ['a', 'b', 'c', 'd', 'e', 'f', 'g'] as const;

type EnergyLabel = (typeof ENERGY_LABELS)[number];

const ENERGY_LABEL_ICON_WIDTH = 57;

/** `public/assets/eu-guarantee-labels` holds one icon per whole year, 1-year through 99-year. */
const MIN_GUARANTEE_YEARS = 1;
const MAX_GUARANTEE_YEARS = 99;

/** The artwork is between 177px and 197px wide; the middle keeps the reserved box honest. */
const GUARANTEE_LABEL_ICON_WIDTH = 181;

/**
 * Specifications arrive as a JSON string, so a malformed value must not take the page down.
 * Numbers come through as either JSON numbers or strings depending on the PIM field.
 */
const readSpecification = (specifications: string | undefined, key: string): string | undefined => {
    if (!specifications) return undefined;

    try {
        const value = (JSON.parse(specifications) as Record<string, unknown>)?.[key];

        if (typeof value === 'string') return value.trim() || undefined;
        if (typeof value === 'number' && Number.isFinite(value)) return String(value);

        return undefined;
    } catch {
        return undefined;
    }
};

const isEnergyLabel = (value: string): value is EnergyLabel =>
    ENERGY_LABELS.some((energyLabel) => energyLabel === value);

const parseEnergyLabel = (specifications: string | undefined): EnergyLabel | null => {
    const label = readSpecification(specifications, 'Energiklasse')?.toLowerCase().charAt(0);

    return label && isEnergyLabel(label) ? label : null;
};

const parseGuaranteeYears = (specifications: string | undefined): number | null => {
    const value = readSpecification(specifications, 'Garantimærkat');

    if (!value) return null;

    const years = Number(value);

    return Number.isInteger(years) && years >= MIN_GUARANTEE_YEARS && years <= MAX_GUARANTEE_YEARS
        ? years
        : null;
};

export interface ProductInfoLabelItem extends ProductInfoLabelProps {
    /** React key, and the reason this entry exists. */
    id: string;
}

/**
 * Every regulatory label the product carries, ready to hand to `<ProductInfoLabel />`. Labels the
 * product does not carry are simply absent.
 *
 * Adding a label means adding an entry here; no new component is needed.
 */
export const useProductInfoLabels = (): ProductInfoLabelItem[] => {
    const { translate } = useTranslation();
    const { product } = usePDPData();
    const { setActiveProductTab, setProductTabToExpand, setInfoLabelDialog } = usePDPActions();

    const specifications = product.c_specifications;

    const energyLabel = useMemo(() => parseEnergyLabel(specifications), [specifications]);
    const guaranteeYears = useMemo(() => parseGuaranteeYears(specifications), [specifications]);

    const labels: ProductInfoLabelItem[] = [];

    const buttonText = translate('pdp.overview.energyLabelButtonText');

    if (energyLabel) {
        // Read once so the closure below narrows to a defined src.
        const labelUrl = product.c_energyLabelUrl;

        labels.push({
            id: 'energy',
            iconSrc: `/assets/energylabels/${energyLabel}.svg`,
            iconAlt: product.c_energyLabelAltText ?? `energy efficiency labels - ${energyLabel}`,
            iconWidth: ENERGY_LABEL_ICON_WIDTH,
            iconLabel: 'Åbn energimærke',
            onIconClick: labelUrl
                ? () =>
                      setInfoLabelDialog({
                          src: labelUrl,
                          alt: product.c_energyLabelAltText,
                          label: 'Energimærke',
                          closeLabel: 'Luk energimærke',
                      })
                : undefined,
            text: buttonText,
            // The energy label's text goes to the specifications rather than to the dialog.
            onTextClick: () => {
                setActiveProductTab(ProductTab.SPECIFICATIONS);
                // Arriving from here means wanting to read the specifications, so the panel
                // opens in full rather than under its "show more" clip.
                setProductTabToExpand(ProductTab.SPECIFICATIONS);
                scrollToElementById(PRODUCT_TABS_ID);
            },
        });
    }

    if (guaranteeYears) {
        const labelUrl = product.c_guaranteeLabelUrl;

        // Unlike the energy label, both the icon and the text open the dialog.
        const openDialog = labelUrl
            ? () =>
                  setInfoLabelDialog({
                      src: labelUrl,
                      alt: product.c_guaranteeLabelAltText,
                      label: 'Garantimærkat',
                      closeLabel: 'Luk garantimærkat',
                  })
            : undefined;

        labels.push({
            id: 'guarantee',
            iconSrc: `/assets/eu-guarantee-labels/${guaranteeYears}-year.svg`,
            iconAlt: product.c_guaranteeLabelAltText ?? `garantimærke - ${guaranteeYears} år`,
            iconWidth: GUARANTEE_LABEL_ICON_WIDTH,
            iconLabel: 'Åbn garantimærkat',
            onIconClick: openDialog,
            text: buttonText,
            onTextClick: openDialog,
        });
    }

    return labels;
};
